// src/utils/authUtils.js
import { auth, db,functions  } from '../firebase';
import {
  sendEmailVerification,
  updateProfile,
  onAuthStateChanged,
  signOut
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

/**
 * Send email verification using custom Resend email from logami.app
 * @param {Object} user - Firebase user object
 * @param {string} redirectUrl - URL to redirect after verification (optional)
 */
export const sendVerificationEmail = async (user, redirectUrl = null) => {
  try {
    // Call our custom Cloud Function that generates link AND sends via Resend
    const sendVerification = httpsCallable(functions, 'sendCustomEmailVerification');
    await sendVerification({
      email: user.email,
      displayName: user.displayName || user.email.split('@')[0]
    });

    console.log('✅ Custom verification email sent from noreply@logami.app');
    return { success: true, message: 'Verification email sent successfully' };
  } catch (error) {
    console.error('❌ Error sending verification email:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create user profile in Firestore
 * @param {Object} user - Firebase user object
 * @param {Object} additionalData - Additional user data
 */
export const createUserProfile = async (user, additionalData = {}) => {
  try {
    const userRef = doc(db, 'users', user.uid);

    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      emailVerified: user.emailVerified,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...additionalData
    };

    await setDoc(userRef, userData);
    return { success: true, data: userData };
  } catch (error) {
    console.error('Error creating user profile:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user profile from Firestore
 * @param {string} userId - User ID
 */
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return { success: true, data: userSnap.data() };
    } else {
      return { success: false, error: 'User profile not found' };
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user profile in Firestore
 * @param {string} userId - User ID
 * @param {Object} updateData - Data to update
 */
export const updateUserProfile = async (userId, updateData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const dataWithTimestamp = {
      ...updateData,
      updatedAt: serverTimestamp()
    };

    await updateDoc(userRef, dataWithTimestamp);
    return { success: true, message: 'Profile updated successfully' };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if user email is verified and update Firestore
 * @param {Object} user - Firebase user object
 */
export const checkAndUpdateEmailVerification = async (user) => {
  try {
    await user.reload(); // Refresh user data

    if (user.emailVerified) {
      // Update Firestore record
      await updateUserProfile(user.uid, {
        emailVerified: true
      });
      return { success: true, verified: true };
    }

    return { success: true, verified: false };
  } catch (error) {
    console.error('Error checking email verification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Set up auth state listener
 * @param {Function} callback - Callback function to handle auth state changes
 */
export const setupAuthListener = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      // User is signed in
      const profileResult = await getUserProfile(user.uid);
      callback({
        user,
        profile: profileResult.success ? profileResult.data : null,
        isAuthenticated: true
      });
    } else {
      // User is signed out
      callback({
        user: null,
        profile: null,
        isAuthenticated: false
      });
    }
  });
};

/**
 * Sign out user
 */
export const signOutUser = async () => {
  try {
    await signOut(auth);
    return { success: true, message: 'Signed out successfully' };
  } catch (error) {
    console.error('Error signing out:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 */
export const validatePassword = (password) => {
  return {
    isValid: password.length >= 6,
    length: password.length >= 6,
    // Add more validation rules as needed
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumbers: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
};

/**
 * Send password reset email using custom Resend email from logami.app
 * @param {string} email - User's email address
 */
export const sendPasswordResetEmailCustom = async (email) => {
  try {
    // Call our custom Cloud Function that generates link AND sends via Resend
    const sendPasswordReset = httpsCallable(functions, 'sendCustomPasswordResetEmail');
    await sendPasswordReset({
      email: email
    });

    console.log('✅ Custom password reset email sent from noreply@logami.app');
    return { success: true, message: 'Password reset email sent successfully' };
  } catch (error) {
    console.error('❌ Error sending password reset email:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send welcome email after user verifies their account
 * @param {Object} user - Firebase user object
 */
export const sendWelcomeEmail = async (user) => {
  try {
    const sendWelcome = httpsCallable(functions, 'sendWelcomeEmail');
    await sendWelcome({
      email: user.email,
      displayName: user.displayName || user.email.split('@')[0],
        uid: user.uid
    });
    console.log('✅ Welcome email sent successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error sending welcome email:', error);
    // Don't fail the login if welcome email fails
    return { success: false, error: error.message };
  }
};